import React, { useState, useMemo, useCallback } from 'react';
import {
  ChevronDown, ChevronUp, Image, Eye, EyeOff,
  CheckSquare, Square, Download, X, ZoomIn, ZoomOut,
  Images, ShieldCheck, ShieldAlert, ShieldX, Loader2,
} from 'lucide-react';
import { validateAllFigures } from './figureValidator';

/* ── Extract all base64 chart strings from nested results ── */
function extractCharts(results) {
  const charts = [];
  if (!results || typeof results !== 'object') return charts;

  const CAPTION_MAP = {
    correlation: {
      chart: 'Pearson Correlation Heatmap with Significance Stars',
      scatter_matrix_chart: 'Pairwise Scatter Matrix',
    },
    regression: { chart: 'Regression Diagnostic Plots (4-panel)' },
    multiple_regression: { chart: 'Multiple Regression: Actual vs Predicted' },
    cluster_analysis: {
      chart: "Ward's Dendrogram & Trait Contribution",
      charts: ["Cluster Membership (Tocher's Method)", "Cluster Dendrogram (UPGMA)", "K-means Clustering on PC1-PC2"],
    },
    path_analysis: { chart: 'Path Analysis: Direct Effects, Comparison & Diagram' },
    pca: { chart: 'PCA Scree Plot & Biplot' },
    d2_analysis: {
      chart: "D² Dendrogram & Trait Contribution",
      charts: ["Mahalanobis D² Distance Heatmap"],
    },
    htp_analysis: {
      chart: 'HTP Trait Distribution & Importance',
      charts: ['Multi-trait Radar Performance Profile', 'Broad-sense Heritability by Trait'],
    },
    molecular_breeding: { chart: 'Manhattan Plot & Top Marker Associations' },
    discriminant_function_analysis: { chart: 'DFA Scatter with 95% Confidence Ellipses' },
  };

  for (const [sectionKey, sectionData] of Object.entries(results)) {
    if (!sectionData || typeof sectionData !== 'object' || sectionData.error) continue;
    const caps = CAPTION_MAP[sectionKey] || {};

    // Main chart
    if (sectionData.chart && typeof sectionData.chart === 'string' && sectionData.chart.length > 100) {
      charts.push({
        id: `${sectionKey}__chart`,
        section: sectionKey,
        base64: sectionData.chart,
        caption: caps.chart || `${sectionKey.replace(/_/g, ' ')} Chart`,
        size: Math.round(sectionData.chart.length * 0.75 / 1024),
      });
    }

    // scatter_matrix_chart (correlation special)
    if (sectionData.scatter_matrix_chart && typeof sectionData.scatter_matrix_chart === 'string' && sectionData.scatter_matrix_chart.length > 100) {
      charts.push({
        id: `${sectionKey}__scatter_matrix_chart`,
        section: sectionKey,
        base64: sectionData.scatter_matrix_chart,
        caption: caps.scatter_matrix_chart || 'Scatter Matrix',
        size: Math.round(sectionData.scatter_matrix_chart.length * 0.75 / 1024),
      });
    }

    // charts array
    if (Array.isArray(sectionData.charts)) {
      sectionData.charts.forEach((c, i) => {
        if (typeof c === 'string' && c.length > 100) {
          const capList = Array.isArray(caps.charts) ? caps.charts : [];
          charts.push({
            id: `${sectionKey}__charts__${i}`,
            section: sectionKey,
            base64: c,
            caption: capList[i] || `${sectionKey.replace(/_/g, ' ')} Chart ${i + 2}`,
            size: Math.round(c.length * 0.75 / 1024),
          });
        }
      });
    }

    // Heritability/Selection/NonParametric/Diversity charts
    if (sectionData.charts && Array.isArray(sectionData.charts) && !caps.charts) {
      // already handled above
    }

    // Nested trait-level charts (e.g., factorial, rbd)
    if (sectionData.traits && typeof sectionData.traits === 'object') {
      for (const [trait, td] of Object.entries(sectionData.traits)) {
        if (td && td.chart && typeof td.chart === 'string' && td.chart.length > 100) {
          charts.push({
            id: `${sectionKey}__traits__${trait}`,
            section: sectionKey,
            base64: td.chart,
            caption: `${trait} Interaction & Main Effects`,
            size: Math.round(td.chart.length * 0.75 / 1024),
          });
        }
      }
    }
  }

  return charts;
}

/* ── Full-size Modal ── */
function ChartModal({ chart, onClose }) {
  const [zoom, setZoom] = useState(1);
  if (!chart) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="chart-modal-overlay">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-[90vw] max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="chart-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E6E1]">
          <p className="text-sm font-semibold text-[#2C2A28] truncate max-w-[60%]">{chart.caption}</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="p-1.5 rounded-lg hover:bg-[#F0EFED] transition" data-testid="zoom-out-btn"><ZoomOut size={16} /></button>
            <span className="text-xs font-mono text-[#8A8580] w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="p-1.5 rounded-lg hover:bg-[#F0EFED] transition" data-testid="zoom-in-btn"><ZoomIn size={16} /></button>
            <a
              href={`data:image/png;base64,${chart.base64}`}
              download={`BKAgriD_${chart.caption.replace(/[^a-zA-Z0-9]/g, '_')}.png`}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] transition"
              data-testid="download-single-chart-btn"
            >
              <Download size={12} /> PNG
            </a>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-red-50 text-[#8A8580] hover:text-red-500 transition" data-testid="modal-close-btn"><X size={18} /></button>
          </div>
        </div>
        <div className="overflow-auto p-4" style={{ maxHeight: 'calc(90vh - 56px)' }}>
          <img
            src={`data:image/png;base64,${chart.base64}`}
            alt={chart.caption}
            className="mx-auto"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.2s ease' }}
            data-testid="modal-chart-image"
          />
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function FigurePreviewPanel({ results, onExcludedChange }) {
  const [expanded, setExpanded] = useState(false);
  const [previewChart, setPreviewChart] = useState(null);

  const allCharts = useMemo(() => extractCharts(results), [results]);
  const [included, setIncluded] = useState(() => {
    const map = {};
    allCharts.forEach(c => { map[c.id] = true; });
    return map;
  });
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  const runValidation = useCallback(async () => {
    setValidating(true);
    try {
      const v = await validateAllFigures(allCharts);
      setValidation(v);
    } finally {
      setValidating(false);
    }
  }, [allCharts]);

  const reportsById = useMemo(() => {
    const m = {};
    (validation?.reports || []).forEach(r => { m[r.id] = r; });
    return m;
  }, [validation]);

  const includedCount = Object.values(included).filter(Boolean).length;
  const totalSize = allCharts.reduce((s, c) => s + (included[c.id] ? c.size : 0), 0);

  const toggleFigure = useCallback((id) => {
    setIncluded(prev => {
      const next = { ...prev, [id]: !prev[id] };
      if (onExcludedChange) {
        const excluded = Object.entries(next).filter(([, v]) => !v).map(([k]) => k);
        onExcludedChange(excluded);
      }
      return next;
    });
  }, [onExcludedChange]);

  const selectAll = () => {
    const map = {};
    allCharts.forEach(c => { map[c.id] = true; });
    setIncluded(map);
    if (onExcludedChange) onExcludedChange([]);
  };
  const deselectAll = () => {
    const map = {};
    allCharts.forEach(c => { map[c.id] = false; });
    setIncluded(map);
    if (onExcludedChange) onExcludedChange(allCharts.map(c => c.id));
  };

  const downloadAllZip = async () => {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    allCharts.forEach((c, i) => {
      if (included[c.id]) {
        const binary = atob(c.base64);
        const bytes = new Uint8Array(binary.length);
        for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
        zip.file(`Figure_${i + 1}_${c.caption.replace(/[^a-zA-Z0-9]/g, '_')}.png`, bytes);
      }
    });
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BKAgriD_All_Figures.zip';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (allCharts.length === 0) return null;

  return (
    <>
      <div className="mb-4 bg-white border border-[#E8E6E1] rounded-2xl shadow-sm overflow-hidden" data-testid="figure-preview-panel">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#FAFAF9] transition-colors"
          data-testid="figure-preview-toggle"
        >
          <div className="flex items-center gap-2.5">
            <Images size={18} className="text-[#2D6A4F]" />
            <span className="text-sm font-semibold text-[#2C2A28]">Figure Preview</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46]" data-testid="figure-count-badge">
              {allCharts.length} figure{allCharts.length !== 1 ? 's' : ''}
            </span>
            {includedCount < allCharts.length && (
              <span className="text-xs text-[#D97706] font-medium">({includedCount} selected)</span>
            )}
          </div>
          {expanded ? <ChevronUp size={18} className="text-[#8A8580]" /> : <ChevronDown size={18} className="text-[#8A8580]" />}
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="border-t border-[#E8E6E1] px-5 pb-5">
            {/* Controls bar */}
            <div className="flex items-center justify-between flex-wrap gap-3 py-3 border-b border-[#F0EFED] mb-4">
              <div className="flex items-center gap-2">
                <button onClick={selectAll} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#2D6A4F]/30 text-[#2D6A4F] hover:bg-[#D1FAE5]/50 transition" data-testid="select-all-btn">
                  <CheckSquare size={12} className="inline mr-1" />Select All
                </button>
                <button onClick={deselectAll} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#8A8580]/30 text-[#8A8580] hover:bg-[#F0EFED] transition" data-testid="deselect-all-btn">
                  <Square size={12} className="inline mr-1" />Deselect All
                </button>
                <span className="text-xs text-[#8A8580] ml-2" data-testid="included-count-text">
                  {includedCount} of {allCharts.length} figures will be included &bull; ~{totalSize}KB
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={runValidation}
                  disabled={validating}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#B45309] text-white hover:bg-[#D97706] transition disabled:opacity-60"
                  data-testid="validate-figures-btn"
                >
                  {validating ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                  {validating ? 'Validating…' : (validation ? 'Re-validate' : 'Validate Figures')}
                </button>
                <button
                  onClick={downloadAllZip}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] transition"
                  data-testid="download-all-zip-btn"
                >
                  <Download size={12} /> Download All as ZIP
                </button>
              </div>
            </div>

            {/* Validation summary */}
            {validation && (
              <div
                className={`mb-4 rounded-xl p-3 border-2 fade-in ${
                  validation.overall === 'pass' ? 'bg-emerald-50 border-emerald-300' :
                  validation.overall === 'warn' ? 'bg-amber-50 border-amber-300' :
                  'bg-red-50 border-red-300'
                }`}
                data-testid="validation-summary"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {validation.overall === 'pass' && <ShieldCheck size={16} className="text-emerald-700" />}
                  {validation.overall === 'warn' && <ShieldAlert size={16} className="text-amber-700" />}
                  {validation.overall === 'fail' && <ShieldX size={16} className="text-red-700" />}
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Figure Quality Report
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span><b className="font-mono">{validation.pass}</b>/{validation.total} publication-ready</span>
                  {validation.warn > 0 && <span className="text-amber-800"><b className="font-mono">{validation.warn}</b> with warnings</span>}
                  {validation.fail > 0 && <span className="text-red-800"><b className="font-mono">{validation.fail}</b> failing</span>}
                  <span className="text-gray-600">Total: {Math.round(validation.totalSizeKB / 1024 * 10) / 10} MB</span>
                </div>
                {validation.fail > 0 && (
                  <p className="text-xs mt-1.5 text-red-800">
                    <b>Warning:</b> {validation.fail} figure{validation.fail > 1 ? 's have' : ' has'} critical issues. Consider regenerating the analysis or excluding these figures before export.
                  </p>
                )}
              </div>
            )}

            {/* Thumbnail Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="figure-grid">
              {allCharts.map((chart, idx) => {
                const isOn = included[chart.id];
                const report = reportsById[chart.id];
                const gradeConfig = report
                  ? (report.grade === 'pass' ? { bg: '#D1FAE5', fg: '#065F46', icon: ShieldCheck, label: 'OK' }
                    : report.grade === 'warn' ? { bg: '#FEF3C7', fg: '#92400E', icon: ShieldAlert, label: `${report.issues.length} warn` }
                    : { bg: '#FEE2E2', fg: '#991B1B', icon: ShieldX, label: 'fail' })
                  : null;
                return (
                  <div
                    key={chart.id}
                    className={`relative group rounded-xl border overflow-hidden transition-all ${isOn ? 'border-[#D1FAE5] bg-white shadow-sm' : 'border-[#E8E6E1] bg-[#F9F8F6] opacity-60'}`}
                    data-testid={`figure-card-${idx}`}
                  >
                    {/* Order badge */}
                    <div className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full bg-[#1B4332] text-white text-[10px] font-bold flex items-center justify-center shadow" data-testid={`figure-number-${idx}`}>
                      {idx + 1}
                    </div>

                    {/* Quality badge */}
                    {gradeConfig && (() => {
                      const GradeIcon = gradeConfig.icon;
                      return (
                        <div
                          className="absolute top-9 left-2 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold shadow-sm"
                          style={{ background: gradeConfig.bg, color: gradeConfig.fg }}
                          title={report.issues.map(i => `[${i.level}] ${i.msg}`).join('\n') || 'Publication-ready'}
                          data-testid={`figure-quality-${idx}`}
                        >
                          <GradeIcon size={9} /> {gradeConfig.label}
                        </div>
                      );
                    })()}

                    {/* Toggle switch */}
                    <button
                      onClick={() => toggleFigure(chart.id)}
                      className={`absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold transition-all shadow-sm ${
                        isOn ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEE2E2] text-[#991B1B]'
                      }`}
                      data-testid={`figure-toggle-${idx}`}
                    >
                      {isOn ? <Eye size={10} /> : <EyeOff size={10} />}
                      {isOn ? 'Included' : 'Excluded'}
                    </button>

                    {/* Thumbnail image */}
                    <div
                      className="cursor-pointer h-[150px] flex items-center justify-center bg-[#FAFAF9] overflow-hidden"
                      onClick={() => setPreviewChart(chart)}
                      data-testid={`figure-thumb-${idx}`}
                    >
                      <img
                        src={`data:image/png;base64,${chart.base64}`}
                        alt={chart.caption}
                        className="max-h-[150px] w-auto object-contain"
                        loading="lazy"
                      />
                    </div>

                    {/* Caption + size */}
                    <div className="px-3 py-2 border-t border-[#F0EFED]">
                      <p className={`text-[11px] font-medium leading-tight ${isOn ? 'text-[#2C2A28]' : 'text-[#8A8580] line-through'}`} data-testid={`figure-caption-${idx}`}>
                        {chart.caption}
                      </p>
                      <div className="flex items-center justify-between mt-0.5">
                        <p className="text-[9px] text-[#B0ACA8]" data-testid={`figure-size-${idx}`}>
                          {report?.dimensions?.width && `${report.dimensions.width}×${report.dimensions.height}px · `}{chart.size}KB
                        </p>
                      </div>
                      {report && report.issues.length > 0 && (
                        <ul className="mt-1 space-y-0.5" data-testid={`figure-issues-${idx}`}>
                          {report.issues.slice(0, 3).map((issue, j) => (
                            <li key={j} className={`text-[9px] leading-snug ${issue.level === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>
                              {issue.level === 'critical' ? '●' : '○'} {issue.msg}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {previewChart && <ChartModal chart={previewChart} onClose={() => setPreviewChart(null)} />}
    </>
  );
}

export { extractCharts };
